<template>
  <div class="chart-item-quad">
    <div class="wrapper">
      <div class="chart-wrapper">
        <div class="spacer" v-if="reports.length === 3 && showResponses === false" />
        <div class="quad-item" v-if="reports.length < 4 && showResponses" :style="responseStyle()">
          <component
            :is="'RangeMap'"
            :progress="totalResponses"
            :numberVal="totalResponses"
            :hideRing="true"
            :hidePercent="true" />
          <div class="chart-title">
            <h4>Total Responses</h4>
          </div>
        </div>
        <div class="quad-item" v-if="reports.length > 0">
          <div class="quad-annotation" v-if="isAnnotated(0)">
            {{getAnnotation(0)}}
          </div>
          <component
            :is="getComponent(0)"
            :isDysphotopsia="isDysphotopsia(0)"
            :progress="getPercent(0)"
            :previous="previousValue"
            :refraction="refraction"
            :previousRefraction="previousRefraction"
            :textValue="getName(0)"
            :numberVal="numberVal"
            :percentColorOverride="percentColorOverride"
            :hideRing="true" />
          <div class="chart-title">
            <h4>{{getName(0)}}</h4>
          </div>
        </div>
        <div class="quad-item" v-if="reports.length > 1">
          <div class="quad-annotation" v-if="isAnnotated(1)">
            {{getAnnotation(1)}}
          </div>
          <component
            :is="getComponent(1)"
            :isDysphotopsia="isDysphotopsia(1)"
            :progress="getPercent(1)"
            :previous="previousValue"
            :refraction="refraction"
            :previousRefraction="previousRefraction"
            :textValue="getName(1)"
            :numberVal="numberVal"
            :percentColorOverride="percentColorOverride"
            :hideRing="true" />
          <div class="chart-title">
            <h4>{{getName(1)}}</h4>
          </div>
        </div>
        <div class="quad-item" v-if="reports.length > 2">
          <div class="quad-annotation" v-if="isAnnotated(2)">
            {{getAnnotation(2)}}
          </div>
          <component
            :is="getComponent(2)"
            :isDysphotopsia="isDysphotopsia(2)"
            :progress="getPercent(2)"
            :previous="previousValue"
            :refraction="refraction"
            :previousRefraction="previousRefraction"
            :textValue="getName(2)"
            :numberVal="numberVal"
            :percentColorOverride="percentColorOverride"
            :hideRing="true" />
          <div class="chart-title">
            <h4>{{getName(2)}}</h4>
          </div>
        </div>
        <div class="quad-item" v-if="reports.length > 3">
          <div class="quad-annotation" v-if="isAnnotated(3)">
            {{getAnnotation(3)}}
          </div>
          <component
            :is="getComponent(3)"
            :isDysphotopsia="isDysphotopsia(3)"
            :progress="getPercent(3)"
            :previous="previousValue"
            :refraction="refraction"
            :previousRefraction="previousRefraction"
            :textValue="getName(3)"
            :numberVal="numberVal"
            :percentColorOverride="percentColorOverride"
            :hideRing="true" />
          <div class="chart-title">
            <h4>{{getName(3)}}</h4>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import RangeMap from 'raypro_ui/src/components/ui/charts/PieProgressChart'
  import MultipleAnswers from 'raypro_ui/src/components/ui/charts/MultipleAnswers'
  import NumberChart from 'raypro_ui/src/components/ui/charts/NumberChart'
  import TextChart from 'raypro_ui/src/components/ui/charts/TextChart'
  import PatientNumbers from 'raypro_ui/src/components/ui/charts/PieNumberProgressChart'
  import {chartCalcs} from 'raypro_ui/src/mixins/chartCalcs'

  export default {
    mixins: [chartCalcs],
    components: {
      'AdminRangeMap': RangeMap,
      RangeMap,
      'AdminBooleanAnswers': TextChart,
      'BooleanAnswers': TextChart,
      'AdminMultipleAnswers': MultipleAnswers,
      MultipleAnswers,
      'AdminIOLNumbers': NumberChart,
      'IOLNumbers': NumberChart,
      'AdminPatientNumbers': PatientNumbers,
      PatientNumbers,
      'AdminUserNumbers': PatientNumbers,
    },
    props: {
      data: {type: [Object, Array], required: true},
      reports: {type: Array, required: true, default: () => []},
      percentColorOverride: {type: String, default: ''},
      showResponses: {type: Boolean, default: false},
      totalResponses: {type: Number, default: 0},
    },
    data() {
      return {
        charts: [
          'RangeMap',
          'MultipleAnswers',
          'NumberChart',
          'PatientNumbers',
          'BooleanAnswers',
          'IOLNumbers',
          'TextChart',
        ],
        noResult: false
      }
    },
    methods: {
      calculatePercentage(period) {
        if(this.data[period] === null) {
          return
        }
        if(Array.isArray(this.data)) {
          if(this.data[0].slug === 'spectacle_independence' ||
             this.data[0].slug === 'satisfaction_with_outcomes') {
            return this.withCategoryCalc(this.data, period)
          }
          if(this.data[0].slug === 'dysphotopsia') {
            return this.dysphotopsia(this.data, period)
          }
          if(this.data[0].slug === 'additional_procedures') {
            return this.booleanAnswers(this.data)
          }
        }

        const scores = this.data[period].data
        const reportModule = this.data.implementation_module

        // TODO: THIS SHOULD BE HANDLED BY THE BACK
        if(Object.values(scores).every((val) => { return val === 0 })) {
          return 'N/A'
        }

        if(this.data[period].data.error) {
          return 'N/A'
        }

        if(reportModule === 'RangeMap' || reportModule === 'AdminRangeMap') {
          return this.rangeMap(scores)
        }

        return 0
      },

      getName(index) {
        let name = this.reports[index].name
        if (this.reports[index].category !== null) {
          name = this.reports[index].category.replace(/\w\S*/g, function(txt) {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
          });
        }
        return name.split('|')[0]
      },

      getPercent(index) {
        const scores = this.reports[index]['report_datas'].data
        const reportModule = this.reports[index].implementation_module

        // TODO: THIS SHOULD BE HANDLED BY THE BACK
        if (Object.values(scores).every((val) => val === 0)) {
          return 'N/A'
        }

        if (this.reports[index]['report_datas'].data.error) {
          return 'N/A'
        }

        if (this.reports[index].slug === 'dysphotopsia') {
          return this.dysphotopsia([this.reports[index]])
        }

        if (reportModule === 'RangeMap' || reportModule === 'AdminRangeMap') {
          return this.rangeMap(scores)
        }

        return 0
      },

      getComponent(index) {
        return this.reports[index].implementation_module
        /*if(Array.isArray(this.data)) {
          return this.data[0].implementation_module
        }
        return this.data.implementation_module*/
      },

      isDysphotopsia(index) {
        return this.reports[index].slug === 'dysphotopsia'
        /*if(Array.isArray(this.data)) {
          if(this.data[0].slug === 'dysphotopsia') {
            return true
          }
        }*/
      },

      isAnnotated(index) {
        return this.reports[index].category.includes('|')
      },

      getAnnotation(index) {
        return this.reports[index].category.split('|')[1]
      },

      responseStyle() {
        if (this.percentColorOverride) {
          return `background-color: ${this.percentColorOverride};`
        }

        return ''
      }
    },

    computed: {
      currentValue() {
        return this.calculatePercentage('report_datas')
      },

      previousValue() {
        let previousValue = this.calculatePercentage('previous_report_data')

        if(this.data.previous_report_data || Array.isArray(this.data)) {
          let dataArray = []

          if(Array.isArray(this.data)) {
            dataArray = this.data
          } else {
            dataArray.push(this.data)
          }

          for(let index = 0; index < dataArray.length; index++) {
            if(dataArray[index].previous_report_data) {
              if(dataArray[index].previous_report_data.data.error) {
                return 'under_10_answers'
              }
            }
          }
        }

        return typeof previousValue === 'number' && previousValue === this.currentValue ? ++previousValue : previousValue
      },

      calcActive() {
        const reportModule = this.data.implementation_module

        if(reportModule === 'PatientNumbers' || reportModule === 'AdminPatientNumbers' || reportModule === 'AdminUserNumbers') {
          return this.patientNumbers(this.data.report_datas.data)
        }
      },

      refraction() {
        if(!Array.isArray(this.data)) {
          const scores = this.data.report_datas.data

          return this.refractions(scores) || {}
        }
        return {}
      },

      previousRefraction() {
        if(!Array.isArray(this.data)) {
          if(this.data.previous_report_data === null) { return {} }
          const scores = this.data.previous_report_data.data
          let previousRefraction = this.refractions(scores)

          if(typeof previousRefraction === 'number' && previousRefraction === this.refraction) {
            return ++previousRefraction
          }

          return previousRefraction || {}
        }
        return {}
      },

      numberVal() {
        if(!Array.isArray(this.data)) {
          const totalPatients = this.data.report_datas.data.total

          return totalPatients
        }
        return false
      },

      activePatients() {
        if(!Array.isArray(this.data)) {
          const active = this.data.report_datas.data.active

          return active
        }
        return false
      }
    }
  }
</script>

<style lang="sass">
  @import 'raypro_ui/src/styles/base/_variables.sass'
  @import 'raypro_ui/src/styles/helpers/_mixins.sass'
  @import '../../../stylesheets/helpers/mixins'

  .chart-item-quad
    width: 100%
    max-width: 320px
    position: relative
    //background-color: $color-panel-bg
    box-shadow: $color-shadow
    margin-right: 0px    
    margin-bottom: 30px
    border-radius: 20px

    @include media(screen and (min-width: 500px))
      flex-basis: calc(50% - 30px)

    @include media(screen and (min-width: 740px))
      flex-basis: calc(33% - 30px)

    @include media(screen and (min-width: 1000px))
      flex-basis: calc(25% - 30px)

    .wrapper
      display: flex
      flex-direction: row
      flex-wrap: wrap
      max-width: 320px
      align-items: center
      justify-content: center

      .chart-wrapper
        display: flex
        flex-wrap: wrap
        flex-direction: row
        justify-content: center
        gap: 15px

        .spacer
          margin-right: -14px

        .quad-item
          width: 47%
          height: 47%
          text-align: center
          border-radius: 20px
          background: $color-panel-bg

          .quad-annotation
            font-size: 1.1rem
            color: $color-text
            margin: 10px 0 -20px 0

        .chart-inner
          cursor: default
          margin: 15px 0 -30px 0

        .chart-title
          text-align: center
          position: relative
          left: 50%
          width: 150px
          color: $color-text
          transform: translateX(-50%)

          h4
            font-size: 14px
            line-height: 1
            margin: 15px 0 15px 0
            // text-align: center
            font-weight: $font-regular
            @include media($media-small)
              font-size: 14px

          h5
            // text-align: center
            font-size: 13px
            font-weight: $font-regular
            color: $color-placeholder

          .active
            display: flex
            flex-direction: row
            justify-content: center
            align-content: center
            align-items: center

            .active-line
              width: 10px
              border-top: 3px solid $color-primary
              margin-right: 7px

            .active-percent
              font-size: 12px

              @include media($media-small)
                font-size: 10px


            @include media($media-small)
              font-size: 10px


    //@include media($media-small)
    //  font-size: 60px

</style>



// WEBPACK FOOTER //
// src/components/pages/Dashboard/ChartItemQuad_v2.vue